import type { SessionSearchResult } from "@mlx/contracts";

import type {
  WorkspaceChannel,
  WorkspaceSession,
} from "@/lib/mlx-workspace-client";

const ACTIVE_SESSION_STATUSES = new Set([
  "queued",
  "running",
  "paused",
  "waiting_for_input",
  "cancelling",
]);

export function workspaceSessionTitle(session: WorkspaceSession): string {
  return session.title?.trim() || "Untitled session";
}

export function isActiveWorkspaceSession(status: string): boolean {
  return ACTIVE_SESSION_STATUSES.has(status);
}

export function workspaceSessionActionAvailability(status: string) {
  return {
    canCancel:
      status === "queued" ||
      status === "running" ||
      status === "waiting_for_input",
    canPause: status === "running" || status === "waiting_for_input",
    canResume: status === "paused",
  };
}

export function workspaceSessionChannelId(
  session: WorkspaceSession,
): string | null {
  if (session.channelId?.trim()) return session.channelId.trim();
  const client = session.metadata?.client;
  if (client && typeof client === "object" && !Array.isArray(client)) {
    const channelId = (client as Record<string, unknown>).channelId;
    if (typeof channelId === "string" && channelId.trim())
      return channelId.trim();
  }
  const references = session.metadata?.references;
  if (
    references &&
    typeof references === "object" &&
    !Array.isArray(references)
  ) {
    const channelId = (references as Record<string, unknown>).channelId;
    if (typeof channelId === "string" && channelId.trim())
      return channelId.trim();
  }
  return null;
}

export function channelMatchesNavigationQuery(
  channel: WorkspaceChannel,
  query: string,
): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return [
    channel.name,
    channel.purpose,
    channel.visibility,
    channel.status,
    ...(channel.objectTypeNames ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(normalized);
}

/** A session a workflow started for its run: it lives under the workflow, not among the person's conversations. */
export function isWorkflowRunSession(session: Pick<WorkspaceSession, "metadata">): boolean {
  return typeof session.metadata?.workflowId === "string";
}

/**
 * The sidebar's sessions: pinned first, then active and recent conversations.
 * Runs a workflow started stay out of Active and Recent (they read under their
 * workflow); pinning or searching still finds them.
 */
export function mergePinnedSessions(
  projected: WorkspaceSession[],
  pinned: WorkspaceSession[],
): {
  pinned: WorkspaceSession[];
  active: WorkspaceSession[];
  recent: WorkspaceSession[];
} {
  const projectedById = new Map(
    projected.map((session) => [session.id, session]),
  );
  const pinnedRows = pinned.map(
    (session) => projectedById.get(session.id) ?? session,
  );
  const pinnedIds = new Set(pinnedRows.map((session) => session.id));
  const unpinned = projected.filter((session) => !pinnedIds.has(session.id) && !isWorkflowRunSession(session));
  return {
    pinned: pinnedRows,
    active: unpinned.filter((session) =>
      isActiveWorkspaceSession(session.status),
    ),
    recent: unpinned.filter(
      (session) => !isActiveWorkspaceSession(session.status),
    ),
  };
}

export function mergePinnedChannels(
  projected: WorkspaceChannel[],
  pinned: WorkspaceChannel[],
): { pinned: WorkspaceChannel[]; channels: WorkspaceChannel[] } {
  const projectedById = new Map(
    projected.map((channel) => [channel.id, channel]),
  );
  const pinnedRows = pinned
    .map((channel) => projectedById.get(channel.id) ?? channel)
    .filter((channel) => channel.status === "active");
  const pinnedIds = new Set(pinnedRows.map((channel) => channel.id));
  return {
    pinned: pinnedRows,
    channels: projected.filter(
      (channel) => channel.status === "active" && !pinnedIds.has(channel.id),
    ),
  };
}

export function liveSessionSearchResults(
  results: SessionSearchResult[],
  projected: WorkspaceSession[],
): Array<Omit<SessionSearchResult, "session"> & { session: WorkspaceSession }> {
  const projectedById = new Map(
    projected.map((session) => [session.id, session]),
  );
  return results.map((result) => ({
    ...result,
    session:
      projectedById.get(result.session.id) ??
      (result.session as WorkspaceSession),
  }));
}

export type ChannelNavigationGroup = { id: string; name: string; channelIds: readonly string[] };

/**
 * Splits the unpinned channels into the organisation's groups, in the groups'
 * order, and the ungrouped rest. Each section keeps the list's own order, and
 * a group shows even when none of its channels are listed.
 */
export function channelNavigationSections<Group extends ChannelNavigationGroup>(
  channels: readonly WorkspaceChannel[],
  groups: readonly Group[],
): {
  grouped: Array<{ group: Group; channels: WorkspaceChannel[] }>;
  ungrouped: WorkspaceChannel[];
} {
  const groupOf = new Map<string, string>();
  for (const group of groups) {
    for (const channelId of group.channelIds) groupOf.set(channelId, group.id);
  }
  return {
    grouped: groups.map((group) => ({
      group,
      channels: channels.filter((channel) => groupOf.get(channel.id) === group.id),
    })),
    ungrouped: channels.filter((channel) => !groupOf.has(channel.id)),
  };
}
